import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Table, Modal } from 'react-bootstrap';
import { HostInfo, GameSpaceInfo, ApiResponse } from '../../types';
import gameService from '../../services/gameService';

interface GameSpaceManagementProps {
  hostInfo?: HostInfo;
  onRefresh?: () => void;
}

const GameSpaceManagement: React.FC<GameSpaceManagementProps> = ({ hostInfo, onRefresh }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [gameSpaces, setGameSpaces] = useState<GameSpaceInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [currentGameSpace, setCurrentGameSpace] = useState<GameSpaceInfo | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPublic: false,
    allowExternalHost: false
  });

  useEffect(() => {
    loadGameSpaces();
  }, []);

  const loadGameSpaces = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // This is a placeholder - we'll need to implement this endpoint in the backend
      // For now, we'll simulate with a mock response
      // const response = await gameService.getGameSpaces();
      
      // Mock response for development
      const mockResponse: ApiResponse<GameSpaceInfo[]> = {
        success: true,
        data: [
          {
            id: '1',
            name: 'My First Game Space',
            description: 'Default game space',
            isActive: true,
            isPublic: true,
            allowExternalHost: false,
            createdAt: new Date().toISOString(),
            gameCount: 3
          }
        ]
      };
      
      if (mockResponse.success) {
        setGameSpaces(mockResponse.data);
      } else {
        setError(mockResponse.message || 'Failed to load game spaces');
      }
    } catch (err) {
      setError('An unexpected error occurred while loading game spaces');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (gameSpace: GameSpaceInfo) => {
    setCurrentGameSpace(gameSpace);
    setFormData({
      name: gameSpace.name,
      description: gameSpace.description || '',
      isPublic: gameSpace.isPublic,
      allowExternalHost: gameSpace.allowExternalHost
    });
    setShowEditModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleUpdateGameSpace = async () => {
    if (!currentGameSpace) return;
    
    setError(null);
    setSuccess(null);
    
    try {
      const response = await gameService.updateGameSpace(currentGameSpace.id.toString(), {
        name: formData.name,
        description: formData.description,
        isPublic: formData.isPublic,
        allowExternalHost: formData.allowExternalHost
      });
      
      if (response.success) {
        setSuccess('Game space updated successfully!');
        setShowEditModal(false);
        
        // Update the game space in the list
        setGameSpaces(gameSpaces.map(gs => 
          gs.id === currentGameSpace.id ? { ...gs, ...formData } : gs
        ));
        
        if (onRefresh) {
          onRefresh();
        }
      } else {
        setError(response.message || 'Failed to update game space');
      }
    } catch (err) {
      setError('An unexpected error occurred while updating the game space');
      console.error(err);
    }
  };

  const canCreateGameSpace = () => {
    if (!hostInfo) return false;
    
    if (hostInfo.isProUser) {
      return hostInfo.currentGameSpaceCount < hostInfo.maxGameSpaces;
    } else {
      return hostInfo.currentGameSpaceCount === 0;
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h4>Your Game Spaces</h4>
          {hostInfo && (
            <p className="text-muted">
              {hostInfo.isProUser 
                ? `You have ${hostInfo.currentGameSpaceCount} of ${hostInfo.maxGameSpaces} game spaces`
                : 'As a regular host, you can have only one game space'}
            </p>
          )}
        </Col>
        <Col xs="auto">
          <Button 
            variant="primary" 
            disabled={!canCreateGameSpace()}
            // This would link to a create game space page or open a modal
          >
            Create New Game Space
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {gameSpaces.length === 0 ? (
        <Alert variant="info">
          You don't have any game spaces yet. Create your first game space to get started!
        </Alert>
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Public</th>
              <th>External Hosts</th>
              <th>Games</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {gameSpaces.map(gameSpace => (
              <tr key={gameSpace.id}>
                <td>{gameSpace.name}</td>
                <td>
                  <span className={`badge bg-${gameSpace.isActive ? 'success' : 'secondary'}`}>
                    {gameSpace.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <span className={`badge bg-${gameSpace.isPublic ? 'info' : 'secondary'}`}>
                    {gameSpace.isPublic ? 'Public' : 'Private'}
                  </span>
                </td>
                <td>
                  <span className={`badge bg-${gameSpace.allowExternalHost ? 'warning' : 'secondary'}`}>
                    {gameSpace.allowExternalHost ? 'Allowed' : 'Not Allowed'}
                  </span>
                </td>
                <td>{gameSpace.gameCount}</td>
                <td>{new Date(gameSpace.createdAt).toLocaleDateString()}</td>
                <td>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => handleEditClick(gameSpace)}
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Edit Game Space Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Game Space</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Public Game Space"
                name="isPublic"
                checked={formData.isPublic}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Public game spaces can be discovered by other users
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Allow External Hosts"
                name="allowExternalHost"
                checked={formData.allowExternalHost}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Allow other users to host games in this game space
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateGameSpace}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GameSpaceManagement;
